import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronRight, CheckCircle2, ShieldCheck, Phone, Mail, MessageCircle, ArrowRight } from "lucide-react";
import { products, contact } from "../mock";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import CtaCallback from "../components/CtaCallback";
import useSeo from "../hooks/useSeo";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = products.find(p => p.slug === slug);
  useSeo({
    title: product ? product.name : "Product",
    description: product ? `${product.name} — ${product.short} ISO 9001:2015 certified. Custom sizes & wattages, OEM solutions, pan-India delivery.` : "Premium industrial IR heater product.",
    keywords: product ? `${product.name}, ${product.applications.slice(0,4).join(", ")}, industrial IR heater` : "industrial heater",
    image: product?.image,
    path: `/products/${slug}`,
  });
  if (!product) return <Navigate to="/products" replace />;
  const related = products.filter(p => p.slug !== slug);

  return (
    <main>
      <section className="bg-cream border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-6 py-5 text-sm text-neutral-600 flex items-center gap-2">
          <Link to="/" className="hover:text-orange-500">Home</Link>
          <ChevronRight size={14} className="text-orange-500" />
          <Link to="/products" className="hover:text-orange-500">Products</Link>
          <ChevronRight size={14} className="text-orange-500" />
          <span className="text-orange-500 font-semibold">{product.name}</span>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10">
          <div className="bg-cream rounded-2xl p-6 md:p-10 border border-orange-100 relative">
            <div className="absolute top-5 left-5 bg-white shadow-md rounded-full px-3 py-1.5 flex items-center gap-2 text-xs font-semibold">
              <ShieldCheck size={14} className="text-orange-500" /> ISO 9001:2015
            </div>
            <img src={product.image} alt={product.name} className="w-full h-[420px] object-contain" />
          </div>

          <div>
            <p className="text-[11px] tracking-[0.3em] text-orange-600 font-semibold uppercase">Premium Quality</p>
            <h1 className="font-display text-3xl md:text-5xl font-bold mt-3 uppercase">{product.name}</h1>
            <span className="block w-20 h-[3px] bg-orange-500 mt-4" />
            <p className="mt-5 text-neutral-700 leading-relaxed">{product.description}</p>

            <h3 className="mt-7 font-display text-lg uppercase">Key Features</h3>
            <ul className="mt-3 grid sm:grid-cols-2 gap-y-2 gap-x-6">
              {product.features.map(f => (
                <li key={f} className="flex items-start gap-2 text-sm text-neutral-800">
                  <CheckCircle2 size={16} className="text-orange-500 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <h3 className="mt-7 font-display text-lg uppercase">Applications</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {product.applications.map(a => (
                <Badge key={a} variant="secondary" className="bg-cream border border-orange-200 text-neutral-800 rounded-full px-3 py-1.5 font-medium">{a}</Badge>
              ))}
            </div>

            <div className="mt-7 bg-cream border border-orange-100 rounded-xl p-5">
              <p className="text-[11px] tracking-[0.3em] text-orange-600 font-semibold uppercase">Get A Quote</p>
              <Link to="/contact">
                <Button className="mt-3 w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full h-12 uppercase tracking-wider font-semibold">
                  Request Quote <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <div className="mt-3 grid grid-cols-3 gap-3">
                <a href={`tel:${contact.phone}`} className="flex items-center justify-center gap-2 h-11 rounded-full border border-neutral-300 hover:bg-neutral-900 hover:text-white transition-colors text-sm font-semibold uppercase">
                  <Phone size={14} /> Call
                </a>
                <a href={`mailto:${contact.emails[0]}`} className="flex items-center justify-center gap-2 h-11 rounded-full border border-neutral-300 hover:bg-neutral-900 hover:text-white transition-colors text-sm font-semibold uppercase">
                  <Mail size={14} /> Email
                </a>
                <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 h-11 rounded-full bg-green-500 hover:bg-green-600 text-white transition-colors text-sm font-semibold uppercase">
                  <MessageCircle size={14} /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-cream bg-dotted py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-10">
              <p className="text-[11px] tracking-[0.3em] text-orange-600 font-semibold uppercase">Also Available</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 uppercase">Related Products</h2>
              <span className="block w-20 h-[3px] bg-orange-500 mx-auto mt-4" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {related.map(p => (
                <Link to={`/products/${p.slug}`} key={p.slug} className="group lift-on-hover bg-white rounded-2xl overflow-hidden border border-orange-100">
                  <div className="aspect-[4/3] bg-cream flex items-center justify-center overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5 text-center">
                    <h3 className="font-display text-base md:text-lg uppercase">{p.name}</h3>
                    <span className="block w-10 h-[3px] bg-orange-500 mx-auto mt-3" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaCallback />
    </main>
  );
}
